import axios from 'axios';
import { Ref, ref, watch } from 'vue';
import { RouteLocationNormalized, Router, useRoute, useRouter } from 'vue-router';
import { useErrorStore } from '@/stores/errors';

export function useDataTableUtilities() {
  const router = useRouter();
  const route = useRoute();

  const appendFilters = (url: string, filters: Array<any>) => {
    let urlWithFilters = url;
    filters.forEach((filter, index) => {
      const filtersColumn = encodeURIComponent(`filters[${index}][column]`);
      const filtersOperator = encodeURIComponent(`filters[${index}][operator]`);
      const filtersValue = encodeURIComponent(`filters[${index}][value]`);
      urlWithFilters += `${urlWithFilters.includes('?') ? '&' : '?'}${filtersColumn}=${encodeURIComponent(String(filter.column ?? ''))}`;
      urlWithFilters += `&${filtersOperator}=${encodeURIComponent(String(filter.operator ?? ''))}`;
      urlWithFilters += `&${filtersValue}=${encodeURIComponent(String(filter.value ?? ''))}`;
    });
    return urlWithFilters;
  };
  const appendRecordLimit = (url: string, recordLimit: string) => {
    let urlWithRecordLimit = url.includes('?') ? `${url}&` : `${url}?`;
    urlWithRecordLimit += `per_page=${encodeURIComponent(recordLimit)}`;
    return urlWithRecordLimit;
  };
  const appendSortColumn = (url: string, sortColumn: string, columns: Array<any>, direction: string | number) => {
    const sortColumnIndex = columns.indexOf(sortColumn);
    if (sortColumnIndex >= 0) {
      return `${url}${url.includes('?') ? '&' : '?'}${encodeURIComponent('order[0][column]')}=${sortColumnIndex}&${encodeURIComponent('order[0][dir]')}=${direction}`;
    }
    return url;
  };
  const appendColumns = (url: string, columns: Array<any>) => {
    let urlWithColumns = url;
    columns.forEach((col, index) => {
      const columnsData = encodeURIComponent(`columns[${index}][data]`);
      urlWithColumns += `${urlWithColumns.includes('?') ? '&' : '?'}${columnsData}=${encodeURIComponent(String(col))}`;
    });
    return urlWithColumns;
  };
  const appendSearchString = (url: string, searchString: string) => {
    let urlWithSearchString = url.includes('?') ? `${url}&` : `${url}?`;
    urlWithSearchString += `${encodeURIComponent('search')}=${encodeURIComponent(searchString)}`;
    return urlWithSearchString;
  };

  const appendPagination = (url: string, pageSize: string | number, currentPage: string | number) => {
    let urlWithPagination = url.includes('?') ? `${url}&` : `${url}?`;
    urlWithPagination += `per_page=${pageSize}&page=${currentPage}`;
    return urlWithPagination;
  };

  const appendIncludes = (url: string, includes: Array<string>) => {
    let urlWithIncludes = url;
    if (includes.length > 0) {
      includes.forEach((inc) => {
        if (urlWithIncludes.includes('?')) {
          urlWithIncludes += `&inc[]=${encodeURIComponent(inc)}`;
        }
        else {
          urlWithIncludes += `?inc[]=${encodeURIComponent(inc)}`;
        }
      });
    }
    return urlWithIncludes;
  };

  const appendFields = (url: string, includes: Array<string>) => {
    let urlWithIncludes = url;
    if (includes.length > 0) {
      includes.forEach((inc) => {
        if (urlWithIncludes.includes('?')) {
          urlWithIncludes += `&fields[]=${encodeURIComponent(inc)}`;
        }
        else {
          urlWithIncludes += `?fields[]=${encodeURIComponent(inc)}`;
        }
      });
    }
    return urlWithIncludes;
  };

  const formatSort = (url: string, sortValue: Array<any>) => {
    if (sortValue === undefined) {
      return '';
    }
    return `${url.includes('?') ? '&' : '?'}sort=${
      sortValue.map((element) => (element.order === 'desc' ? `-${element.key}` : element.key))
        .join(',')}`;
  };

  const getDataTableUrl = (
    baseUrl: string,
    includes: Array<string> = [],
    pageSize: number = 25,
    currentPage: number = 1,
    searchString: string | null = null,
    columns: Array<string> = [],
    sort: Array<any> | null = null,
    recordLimit: number | null = null,
    filters: Array<SearchFilter> = [],
    fields = [],
  ) => {
    // Start from provided baseUrl to avoid shadowing vue-router's route
    let url = baseUrl;
    const requiredColumns = Array.isArray(columns) ? columns : [];
    if (includes.length > 0) {
      url = appendIncludes(url, includes);
    }
    if (pageSize && currentPage) {
      url = appendPagination(url, pageSize, currentPage);
    }
    if (searchString !== null && searchString !== '') {
      url = appendSearchString(url, searchString);
    }
    if (requiredColumns.length > 0) {
      url = appendColumns(url, requiredColumns);
    }
    if (sort) {
      url += formatSort(url, sort);
    }
    if (recordLimit !== null && !(pageSize > 0 && currentPage > 0)) {
      url = appendRecordLimit(url, recordLimit.toString());
    }
    if (filters.length) {
      url = appendFilters(url, filters);
    }
    if (fields.length > 0) {
      url = appendFields(url, fields);
    }
    return url;
  };

  const { handleError } = useErrorStore();

  const writableAttributes = ref<string[]>();

  async function loadWritableAttributes(baseUrl: string, modelId: string | number | undefined) {
    const requestUrl = modelId !== undefined && modelId !== null ? `${baseUrl}/writable/${modelId}` : `${baseUrl}/writable`;
    try {
      const response = await axios.get(requestUrl);
      writableAttributes.value = response.data.data;
    }
    catch (error) {
      handleError(error, 'Could not load writable attributes.');
    }
  }

  function canWriteField(fieldName: string): boolean {
    return !writableAttributes.value || writableAttributes.value.includes(fieldName);
  }

  function canWriteAnyField(fieldNames: string[]): boolean {
    return fieldNames.some((fieldName) => canWriteField(fieldName));
  }

  function getDeletionUrl(url: string, itemId: string | number): string {
    const temp = url.split('?')[0].replace(/\/$/, '');
    return `${temp}/${itemId}`;
  }

  function normalizeSortArray(sort: string | SortItem[] | SortItem | undefined): SortItem[] {
    if (Array.isArray(sort)) {
      return sort.map(s => {
        if (typeof s === 'string') {
          return { key: s, order: 'asc' };
        }
        if (typeof s.order === 'boolean') {
          return { key: s.key, order: s.order ? 'asc' : 'desc' };
        }
        return { key: s.key, order: s.order === 'desc' ? 'desc' : 'asc' };
      });
    }
    else if (typeof sort === 'string') {
      return [{ key: sort, order: 'asc' }];
    }
    else {
      return [];
    }
  }

  function deleteParam(param: string, route: RouteLocationNormalized, router: Router) {
    const query = { ...route.query };
    delete query[param];
    router.replace({ query });
  }

  function getStringFromQuery(val: string | string[] | undefined, fallback: string | undefined = undefined): string | undefined {
    if (Array.isArray(val))
      return val[0] ?? fallback;
    if (typeof val === 'string')
      return val;
    return fallback;
  }
  // Overload for asNum = true
  function getArrayFromQuery(
    _val: string | string[] | number | number[] | undefined,
    _fallback: number[] | undefined,
    _asNum: true,
  ): number[] | undefined;
  // Overload for asNum = false or omitted
  function getArrayFromQuery(
    _val: string | string[] | number | number[] | undefined,
    _fallback?: string[] | undefined,
    _asNum?: false,
  ): string[] | undefined;
  // Implementation
  function getArrayFromQuery(
    val: string | string[] | number | number[] | undefined,
    fallback?: string[] | number[] | undefined,
    asNum?: boolean,
  ): string[] | number[] | undefined {
    const toParts = (input: string | number): string[] =>
      input.toString()
        .split(',')
        .map(s => s.trim())
        .filter(Boolean);

    if (val == null)
      return fallback as any;

    // Normalise to array of strings
    const parts = Array.isArray(val)
      ? val.flatMap(toParts)
      : toParts(val);

    if (!parts.length)
      return fallback as any;

    if (asNum) {
      const nums = parts
        .map(p => Number.parseInt(p, 10))
        .filter(n => !Number.isNaN(n));
      return (nums.length ? nums : fallback) as any;
    }

    return parts as any;
  }

  function getIntFromQuery(val: string | string[] | undefined, fallback: number | undefined = undefined): number | undefined {
    const str = getStringFromQuery(val, fallback?.toString() ?? undefined);
    if (str == null) {
      return fallback;
    }
    const parsed = Number.parseInt(str, 10);
    return Number.isNaN(parsed) ? fallback : parsed;
  }

  // Strongly-typed helpers built on getArrayFromQuery
  function getStringArrayFromQuery(
    val: string | string[] | number | number[] | undefined,
    fallback: string[] = [],
  ): string[] {
    return (getArrayFromQuery(val, fallback, false) ?? fallback) as string[];
  }

  function getNumberArrayFromQuery(
    val: string | string[] | number | number[] | undefined,
    fallback: number[] = [],
  ): number[] {
    return (getArrayFromQuery(val, fallback, true) ?? fallback) as number[];
  }

  function watchSortBy(targetRef: Ref<SortItem[]>) {
    // Initialize sortBy from query param
    if (route.query.sort) {
      const val = Array.isArray(route.query.sort) ? route.query.sort[0] : route.query.sort;
      const fields = val.split(',').map((s: string) => s.trim()).filter(Boolean);
      targetRef.value = fields.map(f => {
        if (f.startsWith('-')) {
          return { key: f.slice(1), order: 'desc' };
        }
        else {
          return { key: f, order: 'asc' };
        }
      });
    }
    // Convert array format back to string for query param
    watch(targetRef, newVal => {
      const normalized = normalizeSortArray(newVal);
      const routeVal = getStringFromQuery(route?.query?.sort, '');
      let sortString = '';
      if (Array.isArray(normalized) && normalized.length > 0) {
        sortString = normalized.map(s => (s.order === 'desc' ? '-' : '') + s.key).join(',');
      }
      if (sortString !== routeVal) {
        if (sortString) {
          router.replace({ query: { ...route?.query, sort: sortString } });
        }
        else {
          deleteParam('sort', route, router);
        }
      }
    });

    // No updateSortBy needed; handled by v-model and watcher
    // Convert query string (field or -field) to array format for datatable
    watch(() => route.query.sort, newSortQuery => {
      const val = getStringFromQuery(newSortQuery, '');
      if (!val) {
        targetRef.value = [];
        return;
      }
      const fields = val.split(',').map(f => f.trim()).filter(Boolean);
      targetRef.value = fields.map(f => {
        if (f.startsWith('-')) {
          return { key: f.slice(1), order: 'desc' };
        }
        else {
          return { key: f, order: 'asc' };
        }
      });
    });
  }

  // Helper for shallow array equality (order-sensitive)
  function arraysEqual(a?: Array<unknown>, b?: Array<unknown>): boolean {
    if (a === b) {
      return true;
    }
    if (!a || !b) {
      return false;
    }
    if (a.length !== b.length) {
      return false;
    }
    for (let i = 0; i < a.length; i += 1) {
      if (a[i] !== b[i]) {
        return false;
      }
    }
    return true;
  }

  function watchArray(targetRef: Ref<Array<any>>, key: string, defaultValue: Array<any> = [], asNumber: boolean = false) {
    watch(() => route.query[key], newVal => {
      const val = getArrayFromQuery(newVal as any, defaultValue as any, asNumber as any);
      if (!arraysEqual(val, targetRef.value)) {
        // Cast because getArrayFromQuery returns string[]
        targetRef.value = (val ?? []) as any[];
      }
    });

    watch(targetRef, newVal => {
      const routeVal = getArrayFromQuery(route.query[key] as any, defaultValue as any, asNumber as any);
      if (!arraysEqual(newVal, routeVal)) {
        if (Array.isArray(newVal) && newVal.length > 0) {
          const csv = newVal.join(',');
          router.replace({ query: { ...route.query, [key]: csv } });
        }
        else {
          deleteParam(key, route, router);
        }
      }
    });
  }
  function watchString(targetRef: Ref<string>, key: string, defaultTab: string | undefined = undefined) {
    watch(() => route.query[key], newVal => {
      const val = getStringFromQuery(newVal, defaultTab);
      if (val !== targetRef.value) {
        targetRef.value = val;
      }
    });

    watch(targetRef, newVal => {
      const routeVal = getStringFromQuery(route.query[key], defaultTab);
      if (newVal !== routeVal) {
        if (newVal) {
          router.replace({ query: { ...route.query, [key]: newVal } });
        }
        else {
          deleteParam(key, route, router);
        }
      }
    });
  }

  function watchInt(targetRef: Ref<number>, key: string, defaultValue: number | undefined = undefined) {
    watch(() => route.query[key], newVal => {
      const val = Number.parseInt(getStringFromQuery(newVal, defaultValue?.toString())) || defaultValue;
      if (val !== targetRef.value) {
        targetRef.value = val;
      }
    });

    watch(targetRef, newVal => {
      const routeVal = Number.parseInt(getStringFromQuery(route.query[key], defaultValue?.toString())) || defaultValue;
      if (newVal !== routeVal) {
        if (newVal !== undefined && newVal !== null) {
          router.replace({ query: { ...route.query, [key]: newVal } });
        }
        else {
          deleteParam(key, route, router);
        }
      }
    });
  }

  const currentPage = ref<number>(getIntFromQuery(route?.query?.page, 1));
  const search = ref<string>(getStringFromQuery(route?.query?.search, ''));
  const tab = ref<string>(getStringFromQuery(route?.query?.tab, ''));
  const itemsPerPage = ref<number>(getIntFromQuery(route?.query?.per_page, 25));
  return {
    getDataTableUrl,
    currentPage,
    search,
    tab,
    itemsPerPage,
    getIntFromQuery,
    getStringArrayFromQuery,
    getNumberArrayFromQuery,
    normalizeSortArray,
    watchSortBy,
    watchString,
    watchInt,
    watchArray,
    getDeletionUrl,
    getStringFromQuery,
    appendFilters,
    appendRecordLimit,
    appendColumns,
    appendSortColumn,
    appendSearchString,
    appendPagination,
    appendIncludes,
    appendFields,
    canWriteField,
    canWriteAnyField,
    loadWritableAttributes,
    deleteParam,
  };
}
